import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme
} from '@mui/material'
import { Aurora, AuroraAwhpEvent, AuroraAwhpData, Sensor } from '../types'
import { useTranslation } from 'react-i18next'
import { AcUnit, OpenInNew, WbSunny } from '@mui/icons-material'
import AuroraStatusIndicator from './AuroraStatusIndicator'
import { formatPercentage, formatPower, formatTemperatureDelta } from '../utils/string'
import { cyan, orange } from '@mui/material/colors'
import { subMinutes } from 'date-fns'
import {
  calculateAuroraHeatOutput,
  calculateAuroraElectricPower,
  calculateAuroraHeatOutputPercentage
} from '../utils/aurora'
import { NavLink } from 'react-router-dom'

function CardBackdrop() {
  return (
    <img
      src="/static/aurora/aurora_awhp.jpeg"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0.2,
        zIndex: 0
      }}
    />
  )
}

export interface AuroraStackCardProps {
  aurora: Aurora
  auroraAwhpData: AuroraAwhpData | null
  auroraAwhpActiveEvents: AuroraAwhpEvent[]
  sensor: Sensor | null
}

export default function AuroraStackCard({
  aurora,
  auroraAwhpData,
  auroraAwhpActiveEvents,
  sensor
}: AuroraStackCardProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The heat output.
   */
  const heatOutput =
    auroraAwhpData !== null ? calculateAuroraHeatOutput(aurora, auroraAwhpData) : null

  /**
   * The heat output percentage.
   */
  const heatOutputPercentage =
    auroraAwhpData !== null ? calculateAuroraHeatOutputPercentage(aurora, auroraAwhpData) : null

  /**
   * The electric power.
   */
  const electricPower =
    auroraAwhpData !== null
      ? calculateAuroraElectricPower(aurora, auroraAwhpData, sensor ?? undefined)
      : null

  /**
   * The temperature delta.
   */
  const temperatureDelta =
    auroraAwhpData !== null
      ? auroraAwhpData.temperature_primary_supply - auroraAwhpData.temperature_primary_return
      : null

  /**
   * Indicates if the unit is online.
   */
  const isRunning =
    auroraAwhpData !== null && new Date(auroraAwhpData.time) > subMinutes(new Date(), 3)

  /**
   * Indicates if the unit is defrosting.
   */
  const isDefrosting = auroraAwhpActiveEvents.some((event) => event.key === 'DEFROST')

  /**
   * Indicates if the unit is cooling.
   */
  const isCooling = false

  /**
   * Indicates if the unit is heating.
   */
  const isHeating = false

  /**
   * The status.
   */
  const status = isDefrosting ? t('defrosting') : isRunning ? t('running') : t('offline')

  /**
   * The color of the status text.
   */
  const statusColor = isDefrosting ? 'info.light' : isRunning ? 'success.light' : 'error.light'

  return (
    <Card
      sx={{
        display: 'flex',
        flexFlow: 'column',
        position: 'relative',
        height: '100%'
      }}
    >
      <CardBackdrop />
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" gap={2}>
            <AuroraStatusIndicator
              aurora={aurora}
              auroraAwhpData={auroraAwhpData}
              sx={{ fontSize: 12 }}
            />
            <Typography variant="h6">{aurora?.asset?.name ?? aurora.id}</Typography>
          </Stack>
        }
        subheader={aurora.id}
        action={
          <Tooltip title={t('actions:showData')}>
            <IconButton component={NavLink} to={`/auroras/${aurora.id}/data`}>
              <OpenInNew htmlColor={theme.palette.text.secondary} />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent sx={{ flexGrow: 1, py: 0, zIndex: 1 }}>
        <Stack direction="column" justifyContent="flex-end" gap={4} sx={{ height: '100%' }}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AuroraStackCardStatsBox
                  label={t('heatOutput')}
                  value={formatPower(heatOutput, true, 1)}
                />
              </Grid>

              <Grid item xs={6}>
                <AuroraStackCardStatsBox
                  label={t('electricPower')}
                  value={formatPower(electricPower, true, 1)}
                />
              </Grid>

              <Grid item xs={6}>
                <AuroraStackCardStatsBox
                  label="ΔT"
                  value={formatTemperatureDelta(temperatureDelta, true, 1)}
                />
              </Grid>

              <Grid item xs={6}>
                <AuroraStackCardStatsBox label={t('status')} value={status} color={statusColor} />
              </Grid>
            </Grid>
          </Box>

          <Box>
            {heatOutputPercentage !== null && (
              <Stack direction="row" gap={4} justifyContent="flex-start" alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={heatOutputPercentage}
                  sx={{ width: '100%', height: 8, borderRadius: 8 }}
                />
                <Typography color="text.secondary">
                  {formatPercentage(heatOutputPercentage)}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" gap={4} mt={2}>
              {isCooling && (
                <Tooltip title={t('cooling')}>
                  <AcUnit htmlColor={cyan[500]} />
                </Tooltip>
              )}

              {isHeating && (
                <Tooltip title={t('heating')}>
                  <WbSunny htmlColor={orange[500]} />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

interface AuroraStackCardStatsBoxProps {
  label: string
  value: string
  color?: TypographyProps['color']
}

function AuroraStackCardStatsBox({
  label,
  value,
  color = 'text.primary'
}: AuroraStackCardStatsBoxProps) {
  return (
    <Box>
      <Typography color="text.secondary">{label}</Typography>
      <Typography variant="h6" color={color}>
        {value}
      </Typography>
    </Box>
  )
}
